import { Log, User, UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { z } from 'zod'
import { TopAuthClient } from './types/TopAuthClient'
import { TopAuthCredentials } from './types/TopAuthCredentials'
import { TopAuthOptions } from './types/TopAuthOptions'

export function createAuthClient(options: TopAuthOptions): TopAuthClient {
  const manager = new UserManager({
    client_id: options.identifier,
    authority: options.authority,
    redirect_uri: window.location.origin,
    post_logout_redirect_uri: window.location.origin,
    response_mode: 'query',
    response_type: 'code',
    scope: options.scopes.join(' '),
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  })

  Log.setLogger(console)
  Log.setLevel(Log.WARN)

  return {
    init: async () => {
      const credentials = await initFromRedirect()
      if (credentials) {
        return credentials
      }

      return getCredentialsOrEmpty()
    },

    login: async () => {
      const state: State = {
        redirect: window.location.href,
      }

      await manager.signinRedirect({
        state,
        redirect_uri: window.location.origin,
      })

      return getCredentials()
    },

    logout: async () => {
      await manager.signoutRedirect()
    },

    getCredentials: async () => {
      return getCredentials()
    },
  }

  async function initFromRedirect(): Promise<TopAuthCredentials | undefined> {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('code')) {
      return undefined
    }

    try {
      const user = await manager.signinCallback()
      if (user) {
        const state = stateSchema.safeParse(user.state).data
        if (state?.redirect) {
          window.history.replaceState(null, '', state.redirect)
        } else {
          window.history.replaceState(null, '', window.location.origin)
        }

        console.log(`User logged in from callback.`)
        return toCredentials(user)
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'No state in response') {
          return
        }
      }

      console.info('Failed to init auth from callback. ', error)
      return
    }
  }

  async function getCredentials(): Promise<TopAuthCredentials> {
    const credential = await getCredentialsOrEmpty()
    if (credential) {
      return credential
    }

    throw new Error('Credentials not found. Please login.')
  }

  async function getCredentialsOrEmpty(): Promise<TopAuthCredentials | undefined> {
    const user = await manager.getUser()
    if (user) {
      return toCredentials(user)
    }

    return undefined
  }

  function toCredentials(user: User): TopAuthCredentials {
    return {
      accessToken: user.access_token,
      idToken: user.id_token,
    }
  }
}

type State = {
  readonly redirect: string
}

const stateSchema = z.object({
  redirect: z.string(),
})
